/* @flow */
// Helpers for when the player runs inside the GymRadio phone app (Capacitor),
// rather than in a browser. In a browser every function here is a no-op.

export const APP_CALLBACK_URL = 'com.gymradio.app://callback'

export const isApp = (): boolean => {
  try {
    const C = window.Capacitor
    return !!(C && typeof C.isNativePlatform === 'function' && C.isNativePlatform())
  } catch (e) {
    return false
  }
}

// The phone app's version (e.g. "0.13"), which the app adds to its browser
// identity. Empty in a browser.
export const appVersion = (): string => {
  if (!isApp()) return ''
  try {
    const m = (navigator.userAgent || '').match(/GymRadioApp\/([\w.]+)/)
    return m ? m[1] : ''
  } catch (e) {
    return ''
  }
}

// The native audio engine (plays songs, ads and announcements with the screen
// locked, with lock-screen controls). Undefined in a browser or an older app.
// The app's plugins only appear in Capacitor.Plugins once registered from
// JavaScript, so register it here when the app says it has the engine.
let engine
let reported = false
export const nativeAudio = () => {
  if (!isApp()) return undefined
  if (engine) return engine
  try {
    const C = window.Capacitor
    const listed = (C.Plugins || {}).GymRadioAudio
    if (listed) {
      engine = listed
    } else if (typeof C.isPluginAvailable === 'function' && C.isPluginAvailable('GymRadioAudio') &&
      typeof C.registerPlugin === 'function') {
      engine = C.registerPlugin('GymRadioAudio')
    }
  } catch (e) {
    engine = undefined
  }
  if (!reported) {
    reported = true
    // eslint-disable-next-line no-console
    console.log(`[GymRadio] native audio engine: ${engine ? 'on' : 'off'}`)
  }
  return engine
}

export const hasNativeAudio = (): boolean => !!nativeAudio()

// Google refuses sign-in inside apps, so the app sends that sign-in to a secure
// browser panel. The app reopens the player with ?applogin=google; we start the
// Google sign-in from here (so this page holds the sign-in state) with the
// app's own return address. The app catches the authorize page and the return.
export const takeAppLoginRequest = (): string => {
  if (!isApp()) return ''
  try {
    const params = new URLSearchParams(window.location.search)
    const which = params.get('applogin') || ''
    if (which) {
      params.delete('applogin')
      const qs = params.toString()
      window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash)
    }
    return which
  } catch (e) {
    return ''
  }
}

// Diagnostics: the app keeps a short log of what the player did (songs, pauses
// and why, errors). Tapping the GymRadio logo 5 times shares it, together with
// the audio engine's own log, so problems on a real phone can be looked into.
const logLines = []
export const appLog = (...parts: any[]) => {
  if (!isApp()) return
  const line = `${new Date().toISOString().slice(11, 19)} ${parts.map((p) => String(p)).join(' ')}`
  logLines.push(line)
  if (logLines.length > 300) logLines.shift()
  try { console.log('[GymRadio]', line) } catch (e) {} // eslint-disable-line no-console
}

export const shareAppLog = () => {
  const e = nativeAudio()
  if (!e) return
  e.shareLog({ text: logLines.join('\n') }).catch(() => {})
}

let logoTaps = []
export const onLogoTap = (ev: any) => {
  if (!isApp()) return
  if (ev && ev.preventDefault) ev.preventDefault()
  const now = Date.now()
  logoTaps = logoTaps.filter((t) => now - t < 4000)
  logoTaps.push(now)
  if (logoTaps.length >= 5) {
    logoTaps = []
    shareAppLog()
  }
}
